import React from 'react';
import { MapPin } from 'lucide-react';

/**
 * SiteMapPreview Component
 * Displays an OpenStreetMap embed showing the site location
 * 
 * Features:
 * - Placeholder state when no coordinates available
 * - OpenStreetMap embed iframe with marker
 * - Coordinate overlay with exact values
 * - Accessible with proper ARIA labels
 */
const SiteMapPreview = ({ latitude, longitude, height = '250px', siteName = 'Site' }) => {
  const hasCoordinates = latitude !== null && longitude !== null && 
    latitude !== undefined && longitude !== undefined &&
    !isNaN(latitude) && !isNaN(longitude);
  
  // Validate coordinates are within reasonable bounds
  const isValidCoordinates = hasCoordinates && 
    Math.abs(latitude) <= 90 && 
    Math.abs(longitude) <= 180;
  
  if (!isValidCoordinates) {
    return (
      <div 
        data-testid="map-preview-placeholder"
        className="rounded-lg border-2 border-dashed border-slate-600 bg-slate-800/50 flex flex-col items-center justify-center text-gray-400"
        style={{ height }}
        role="img"
        aria-label="Map preview placeholder - coordinates not yet calculated"
      >
        <svg 
          className="w-12 h-12 mb-3 text-slate-600" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={1.5} 
            d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" 
          />
        </svg>
        <p className="text-sm font-medium">Map Preview</p>
        <p className="text-xs text-gray-500 mt-1">Calculate coordinates to view location</p>
      </div>
    );
  }
  
  // Use OpenStreetMap embed for simple, reliable map display
  // Calculate bounding box around the coordinates for better zoom
  const bbox = {
    west: longitude - 0.005,
    south: latitude - 0.003,
    east: longitude + 0.005,
    north: latitude + 0.003
  };
  
  const mapUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox.west}%2C${bbox.south}%2C${bbox.east}%2C${bbox.north}&layer=mapnik&marker=${latitude}%2C${longitude}`;
  
  return (
    <div 
      data-testid="map-preview-container"
      className="rounded-lg overflow-hidden border border-slate-600 relative"
      style={{ height }}
      role="region"
      aria-label={`Map showing location of ${siteName} at coordinates ${latitude.toFixed(5)}, ${longitude.toFixed(5)}`}
    >
      <iframe
        title={`Map showing ${siteName} location`}
        src={mapUrl}
        style={{ 
          border: 0, 
          width: '100%', 
          height: '100%'
        }}
        allowFullScreen
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        aria-describedby="map-coordinates"
      />
      {/* Coordinate overlay */}
      <div 
        id="map-coordinates"
        className="absolute bottom-2 left-2 bg-slate-900/90 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-slate-600 flex items-center gap-2"
      >
        <MapPin className="w-4 h-4 text-purple-400" aria-hidden="true" />
        <span className="text-xs text-gray-300">
          {latitude.toFixed(5)}, {longitude.toFixed(5)}
        </span>
      </div>
      {/* Keyboard accessible link to view full map */}
      <a
        href={`https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}#map=17/${latitude}/${longitude}`}
        target="_blank"
        rel="noopener noreferrer"
        className="absolute bottom-2 right-2 bg-slate-900/90 backdrop-blur-sm px-2 py-1 rounded-lg border border-slate-600 text-xs text-purple-400 hover:text-purple-300 hover:bg-slate-800 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500"
        aria-label="Open location in OpenStreetMap (opens in new tab)"
      >
        Open Map ↗
      </a>
    </div>
  );
};

export default SiteMapPreview;
